import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface PricingCardProps {
  name: string;
  tagline: string;
  price: string;
  priceSubtext?: string;
  bestFor: string;
  topFeatures: string[];
  ctaText: string;
  onCtaClick: () => void;
  isLoading?: boolean;
  highlighted?: boolean;
  /** Optional callout shown above the features list (e.g., CRC perks). */
  perkCallout?: string;
}

export function PricingCard({
  name,
  tagline,
  price,
  priceSubtext,
  bestFor,
  topFeatures,
  ctaText,
  onCtaClick,
  isLoading,
  highlighted,
  perkCallout,
}: PricingCardProps) {
  return (
    <Card
      className={cn(
        "relative flex flex-col h-full transition-shadow",
        highlighted
          ? "border-primary shadow-lg ring-2 ring-primary/20"
          : "border-border"
      )}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-primary text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
          Most Popular
        </div>
      )}

      <CardHeader className="pb-4">
        <div className="text-center">
          <h3 className="text-xl font-bold text-foreground">{name}</h3>
          <p className="text-sm text-muted-foreground">{tagline}</p>
          <div className="mt-4">
            <span className="text-3xl font-bold text-foreground">{price}</span>
            {priceSubtext && (
              <span className="text-sm text-muted-foreground ml-1">
                {priceSubtext}
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col flex-grow pt-0">
        {/* Best for */}
        <p className="text-sm text-muted-foreground mb-4">
          <span className="font-medium text-foreground">Best for:</span> {bestFor}
        </p>

        {/* Optional perk callout (e.g. CRC member benefits) */}
        {perkCallout && (
          <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-medium text-primary">
            {perkCallout}
          </div>
        )}

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-grow">
          {topFeatures.map((feature, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
              <span className="text-sm text-foreground">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="mt-auto">
          <Button
            className={cn("w-full", highlighted && "bg-gradient-primary")}
            variant={highlighted ? "default" : "outline"}
            onClick={onCtaClick}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {ctaText}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
